/**
 * @file: provider.tsx
 *
 * Owns the chat thread list and everything that keeps it current: the
 * initial load, the live WebSocket-event reducer (new/edited messages,
 * new threads, reads), and the local-read bookkeeping that reconciles the
 * per-thread badge with the shared unread total.
 *
 * Scope it to the surface that shows the thread list (the inbox mini-app),
 * NOT app-wide — it is created when that surface opens and torn down when
 * it closes, so the lists don't sit in memory for the whole app session.
 *
 * The app-wide pieces it reads but does NOT own — the unread total, the
 * active room, the WS connection — live on `ChatSessionProvider`
 * (`useChatSession`). The connection lifecycle is `ChatTransport`'s job;
 * this provider only reacts to events that arrive while connected.
 */

import { chatMessagePreview } from '@euler/app/flows/chat/utils/chatMessagePreview';
import { isOwnSystemAction } from '@euler/app/flows/chat/utils/chatNotificationText';
import { chatThreadIsPinned } from '@euler/app/flows/chat/utils/chatThreadDisplay';
import { ChatThreadItem } from '@euler/model/chat';
import { IdType } from '@euler/model/common';
import { useServiceFactory } from '@euler/services/factory';
import { idEquals } from '@euler/utils';
import { useCreation, useWatchValue } from '@euler/utils/hooks';
import {
  FC,
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { BehaviorSubject } from 'rxjs';
import { ChatEvent } from '../session/chatEvents';
import { useChatSession } from '../session/context';
import { ChatThreadsContext } from './context';
import { ChatThreadsStore } from './types';

/** Initial thread-list page size. */
const THREAD_PAGE_SIZE = 50;

export const ChatThreadsProvider: FC<{ children: ReactNode }> = memo(
  ({ children }) => {
    const { chatService } = useServiceFactory();
    const {
      me,
      wsClient,
      chatUnreadCount$,
      activeRoomId$,
      threadReadLocally$,
    } = useChatSession();
    const myPlatformUserId = me.platformUserId;

    const chatThreads$ = useCreation(
      () => new BehaviorSubject<ChatThreadItem[]>([]),
      [],
    );
    // True until the first list fetch resolves — drives the inbox skeleton so
    // a cold load shows placeholders instead of the empty state.
    const loading$ = useCreation(() => new BehaviorSubject(true), []);

    // Threads the user has read locally → maps id to whether its unread
    // has already been subtracted from the tab total. Survives a list
    // (re)load so a read done BEFORE the list arrived (e.g. opening a room
    // from a cold-start push while the inbox is still loading) both clears
    // the per-thread badge AND decrements the tab total — exactly once,
    // the moment we learn the count — instead of waiting for the periodic
    // unread-API reconcile. Cleared per-thread when a new unread lands.
    const locallyReadThreads = useRef<Map<string, boolean>>(new Map());

    const markThreadReadOptimistic = useCallback(
      (threadId: string) => {
        const threads = chatThreads$.getValue();
        const idx = threads.findIndex(t => t.id === threadId);
        if (idx < 0) {
          // Not loaded yet — remember as pending; the tab-total drop is
          // applied when the list loads (see refreshChatThreads).
          locallyReadThreads.current.set(threadId, false);
          return;
        }
        // In the list → we know the delta, so account for it now.
        locallyReadThreads.current.set(threadId, true);
        const thread = threads[idx];
        const delta = thread.unreadCount;
        if (delta <= 0) return;
        const next = [...threads];
        next[idx] = { ...thread, unreadCount: 0, unreadMentionCount: 0 };
        chatThreads$.next(next);
        chatUnreadCount$.next(Math.max(0, chatUnreadCount$.getValue() - delta));
      },
      [chatThreads$, chatUnreadCount$],
    );

    const refreshChatThreads = useCallback(async () => {
      try {
        const result = await chatService.listThreads({
          limit: THREAD_PAGE_SIZE,
        });
        // Re-apply local reads the server fetch may not reflect yet:
        // zero the per-thread badge, and subtract from the tab total
        // for any read not yet accounted for (the cold-start case).
        let totalDrop = 0;
        const items = result.items.map(t => {
          if (!locallyReadThreads.current.has(t.id) || t.unreadCount <= 0) {
            return t;
          }
          if (locallyReadThreads.current.get(t.id) === false) {
            totalDrop += t.unreadCount;
            locallyReadThreads.current.set(t.id, true);
          }
          return { ...t, unreadCount: 0, unreadMentionCount: 0 };
        });
        chatThreads$.next(items);
        if (totalDrop > 0) {
          chatUnreadCount$.next(
            Math.max(0, chatUnreadCount$.getValue() - totalDrop),
          );
        }
      } catch (e) {
        console.error('[chat-threads] refresh threads failed', e);
      } finally {
        if (loading$.getValue()) loading$.next(false);
      }
    }, [chatService, chatThreads$, chatUnreadCount$, loading$]);

    // Toggle pin/mute optimistically, then persist. The list re-sorts off
    // `pinnedAt` (see useThreadList); mute only re-renders the row. On failure
    // revert the single field so the row snaps back.
    const setThreadFlag = useCallback(
      (threadId: string, flag: 'pinned' | 'muted', on: boolean) => {
        const threads = chatThreads$.getValue();
        const idx = threads.findIndex(t => t.id === threadId);
        if (idx < 0) return;
        const prev = threads[idx];
        const patch: Partial<ChatThreadItem> =
          flag === 'pinned'
            ? { pinnedAt: on ? 1 : 0 }
            : { mutedAt: on ? new Date().toISOString() : null };
        const next = [...threads];
        next[idx] = { ...prev, ...patch };
        chatThreads$.next(next);
        const request =
          flag === 'pinned'
            ? chatService.pinThread(threadId, on)
            : chatService.muteThread(threadId, on);
        request.catch(e => {
          console.error('[chat-threads] set flag failed', flag, e);
          const cur = chatThreads$.getValue();
          const i = cur.findIndex(t => t.id === threadId);
          if (i < 0) return;
          const reverted = [...cur];
          reverted[i] =
            flag === 'pinned'
              ? { ...cur[i], pinnedAt: prev.pinnedAt }
              : { ...cur[i], mutedAt: prev.mutedAt };
          chatThreads$.next(reverted);
        });
      },
      [chatThreads$, chatService],
    );

    // Delete the viewer's copy of a thread: drop the row (and its unread from
    // the tab total) immediately, then persist. On failure, refetch to restore
    // the true list rather than guess the pre-delete order. The refetch is
    // last-writer-wins: a pin/mute toggle still in flight on another row would
    // be overwritten by the server's stale value (it re-applies on the next
    // reconcile) — acceptable for a rare delete-failure + concurrent-toggle.
    const deleteThread = useCallback(
      (threadId: string) => {
        const threads = chatThreads$.getValue();
        const removed = threads.find(t => t.id === threadId);
        if (!removed) return;
        chatThreads$.next(threads.filter(t => t.id !== threadId));
        if (removed.unreadCount > 0) {
          chatUnreadCount$.next(
            Math.max(0, chatUnreadCount$.getValue() - removed.unreadCount),
          );
        }
        locallyReadThreads.current.delete(threadId);
        chatService.deleteThread(threadId).catch(e => {
          console.error('[chat-threads] delete thread failed', e);
          void refreshChatThreads();
        });
      },
      [chatThreads$, chatUnreadCount$, chatService, refreshChatThreads],
    );

    // Initial load — the thread list is this provider's own concern, not
    // the transport's. Re-runs on account switch (myPlatformUserId change).
    useEffect(() => {
      if (!myPlatformUserId) return;
      void refreshChatThreads();
    }, [myPlatformUserId, refreshChatThreads]);

    // Live WebSocket event reducer. Subscribes whenever a viewer is
    // present; events only arrive while `wsClient` is connected (the
    // transport owns connect/disconnect). The reducer reads the session's
    // active room and bumps the shared unread total — both owned by
    // `ChatSessionProvider`.
    useEffect(() => {
      if (!myPlatformUserId) return;
      const sub = wsClient.onEvent().subscribe(event => {
        reduceChatEvent(event, myPlatformUserId, {
          chatThreads$,
          chatUnreadCount$,
          activeRoomId$,
          markThreadReadOptimistic,
          refreshChatThreads,
          locallyReadThreads: locallyReadThreads.current,
        });
      });
      return () => sub.unsubscribe();
    }, [
      myPlatformUserId,
      wsClient,
      chatThreads$,
      chatUnreadCount$,
      activeRoomId$,
      markThreadReadOptimistic,
      refreshChatThreads,
    ]);

    // A conversation opened elsewhere in the app (the root-stack chat flow,
    // the floating window) announces its read on the session bus — clear the
    // row's badge now instead of waiting for the server's `thread.read` echo.
    // The list is this provider's concern, so it owns the subscription.
    useEffect(() => {
      const sub = threadReadLocally$.subscribe(markThreadReadOptimistic);
      return () => sub.unsubscribe();
    }, [threadReadLocally$, markThreadReadOptimistic]);

    // Account switch within the same auth-context key — wipe the local
    // list. (The keyed AuthContextProvider remounts us on most switches;
    // this covers the rest.) `!prev` skips both the mount call and the
    // initial login. The shared unread/active-room reset lives on the
    // session provider.
    useWatchValue(myPlatformUserId, prev => {
      if (!prev) return;
      chatThreads$.next([]);
      loading$.next(true);
      locallyReadThreads.current.clear();
    });

    const store = useMemo<ChatThreadsStore>(
      () => ({
        chatThreads$,
        loading$,
        markThreadReadOptimistic,
        refreshChatThreads,
        setThreadFlag,
        deleteThread,
      }),
      [
        chatThreads$,
        loading$,
        markThreadReadOptimistic,
        refreshChatThreads,
        setThreadFlag,
        deleteThread,
      ],
    );

    return (
      <ChatThreadsContext.Provider value={store}>
        {children}
      </ChatThreadsContext.Provider>
    );
  },
);

// ─────────────────────────── live event reducer

interface ReduceCtx {
  chatThreads$: BehaviorSubject<ChatThreadItem[]>;
  chatUnreadCount$: BehaviorSubject<number>;
  activeRoomId$: BehaviorSubject<string | null>;
  markThreadReadOptimistic: (threadId: string) => void;
  /** Re-fetch the list — used to surface a thread an event references but
   * that isn't in the loaded page. */
  refreshChatThreads: () => Promise<void>;
  /** Locally-read threads → total-already-decremented flag; new unread clears it. */
  locallyReadThreads: Map<string, boolean>;
}

function reduceChatEvent(
  event: ChatEvent,
  myPlatformUserId: IdType,
  ctx: ReduceCtx,
): void {
  const { chatThreads$, chatUnreadCount$, activeRoomId$ } = ctx;

  if (event.kind === 'thread.new') {
    // A brand-new thread (first message to a new room, or a freshly
    // created group). The event carries the full inbox row, so we insert
    // it WITHOUT a refetch. Unread is zeroed here — the `message.new`
    // that follows on the same channel does the unread/preview bump, so
    // there's a single counting path.
    const item = event.body.thread;
    const newRoomId = item.room.id;
    const threads = chatThreads$.getValue();
    // Idempotent: skip a duplicate broadcast. Matching on room id too (not
    // just thread id) assumes one thread per room per viewer for the room's
    // life — true today; revisit if a room can ever get a fresh thread id.
    if (threads.some(t => t.id === item.id || t.room?.id === newRoomId)) {
      return;
    }
    const fresh: ChatThreadItem = {
      ...item,
      unreadCount: 0,
      unreadMentionCount: 0,
    };
    // New threads are unpinned → above other unpinned, below pinned.
    chatThreads$.next([
      ...threads.filter(chatThreadIsPinned),
      fresh,
      ...threads.filter(t => !chatThreadIsPinned(t)),
    ]);
    return;
  }

  if (event.kind === 'message.new') {
    const roomId = event.roomId;
    // A system message (members.invited / room.dismissed) has no actor
    // on the envelope, so derive "mine" from the payload: the inviter /
    // owner who triggered it must NOT see their own thread go unread.
    const isMine =
      (event.type === 'user' &&
        idEquals(event.platformUserId, myPlatformUserId)) ||
      isOwnSystemAction(event.body.payload, myPlatformUserId);
    const isActive = activeRoomId$.getValue() === roomId;
    const shouldCountUnread = !isMine && !isActive;

    if (shouldCountUnread) {
      chatUnreadCount$.next(chatUnreadCount$.getValue() + 1);
    }

    const threads = chatThreads$.getValue();
    const idx = threads.findIndex(t => t.room?.id === roomId);
    // A fresh unread message clears the local-read marker so the badge
    // re-appears (and isn't zeroed on the next list reload).
    if (shouldCountUnread && idx >= 0) {
      ctx.locallyReadThreads.delete(threads[idx].id);
    }
    if (idx >= 0) {
      const thread = threads[idx];
      const updated: ChatThreadItem = {
        ...thread,
        unreadCount: shouldCountUnread
          ? thread.unreadCount + 1
          : thread.unreadCount,
        unreadMentionCount:
          event.mentioned && shouldCountUnread
            ? thread.unreadMentionCount + 1
            : thread.unreadMentionCount,
        // Sort + timestamp by the server's send time, not client receive
        // time — clock skew or a delayed delivery would otherwise misorder
        // the thread and disagree with the message bubble's own createdAt.
        latestActivityAt: event.body.createdAt,
        latestMessage: {
          id: event.body.messageId,
          preview: chatMessagePreview(event.body.payload, myPlatformUserId),
          authorName: event.type === 'user' ? event.authorName : null,
          payload: event.body.payload,
          createdAt: event.body.createdAt,
        },
      };
      const pinned = chatThreadIsPinned(updated) ? [updated] : [];
      const unpinnedActive = chatThreadIsPinned(updated) ? [] : [updated];
      const others = threads.filter((_, i) => i !== idx);
      chatThreads$.next([
        ...pinned,
        ...others.filter(chatThreadIsPinned),
        ...unpinnedActive,
        ...others.filter(t => !chatThreadIsPinned(t)),
      ]);
    } else if (shouldCountUnread) {
      // Unread-worthy message for a room not in the loaded page (a thread
      // beyond the first page, or one whose `thread.new` we missed during a
      // WS gap). Refetch so the row surfaces instead of leaving a badge with
      // no inbox entry. In the normal new-thread flow `thread.new` arrives
      // first and inserts the row, so idx >= 0 and this doesn't fire.
      void ctx.refreshChatThreads();
    }
  } else if (event.kind === 'message.edited') {
    const roomId = event.roomId;
    const threads = chatThreads$.getValue();
    const idx = threads.findIndex(t => t.room?.id === roomId);
    if (idx >= 0 && threads[idx].latestMessage?.id === event.body.messageId) {
      chatThreads$.next(
        threads.map((t, i) =>
          i === idx && t.latestMessage
            ? {
                ...t,
                latestMessage: {
                  ...t.latestMessage,
                  preview: chatMessagePreview(
                    event.body.payload,
                    myPlatformUserId,
                  ),
                },
              }
            : t,
        ),
      );
    }
  } else if (event.kind === 'thread.updated') {
    // Routed by intent: a room-info change is room-wide (key on room id,
    // every member has their own thread for it); a flag flip is per-user
    // (key on the actor's own threadId the server stamped). Idempotent on
    // the originating device, which already applied the change optimistically.
    const threads = chatThreads$.getValue();
    if (event.body.intent === 'room-info-updated') {
      const { title, avatarUrl, meta } = event.body;
      const idx = threads.findIndex(t => t.room?.id === event.roomId);
      if (idx < 0) return;
      const next = [...threads];
      next[idx] = {
        ...threads[idx],
        room: {
          ...threads[idx].room,
          title,
          avatarUrl,
          orderSnapshot: meta,
        },
      };
      chatThreads$.next(next);
    } else {
      // Flag flip: pinned/unpinned set pinnedAt (int sentinel); muted/unmuted
      // set mutedAt (ISO or null). The list re-sorts off pinnedAt.
      const idx = threads.findIndex(t => t.id === event.threadId);
      if (idx < 0) return;
      const { intent, timestamp } = event.body;
      const patch: Partial<ChatThreadItem> =
        intent === 'pinned'
          ? { pinnedAt: timestamp }
          : intent === 'unpinned'
          ? { pinnedAt: 0 }
          : intent === 'muted'
          ? { mutedAt: new Date(timestamp).toISOString() }
          : { mutedAt: null };
      const next = [...threads];
      next[idx] = { ...threads[idx], ...patch };
      chatThreads$.next(next);
    }
  } else if (event.kind === 'thread.deleted') {
    // Deleted on another device → drop the row (and its unread from the tab
    // total). A no-op on the device that deleted it (already removed).
    const threads = chatThreads$.getValue();
    const removed = threads.find(t => t.id === event.threadId);
    if (!removed) return;
    chatThreads$.next(threads.filter(t => t.id !== event.threadId));
    if (removed.unreadCount > 0) {
      chatUnreadCount$.next(
        Math.max(0, chatUnreadCount$.getValue() - removed.unreadCount),
      );
    }
    ctx.locallyReadThreads.delete(event.threadId);
  } else if (event.kind === 'thread.read') {
    ctx.markThreadReadOptimistic(event.threadId);
  }
}
